export default {
  "slug": "acceptance-testing-vs-commissioning",
  "title": "Acceptance Testing vs Commissioning: What's the Difference?",
  "description": "Acceptance testing (NFPA 72 Chapter 14) and commissioning (NFPA 3 / NFPA 4) are often confused. Here's how they differ in scope, timing, and documentation — and how they work together on a fire alarm project.",
  "keywords": "fire alarm acceptance testing vs commissioning, NFPA 72 chapter 14 acceptance test, NFPA 3 commissioning, NFPA 4 integrated testing, fire alarm handover, test documentation",
  "faqs": [
    {
      "q": "Aren't acceptance testing and commissioning the same thing?",
      "a": "No. Acceptance testing (NFPA 72 Chapter 14) verifies the installed fire alarm system operates correctly — device by device — at handover. Commissioning (NFPA 3, with integrated testing in NFPA 4) is a broader process that spans design through operation and confirms multiple systems work together as intended."
    },
    {
      "q": "Which one comes first?",
      "a": "Commissioning as a process starts in design and wraps around the whole project. Acceptance testing is a milestone within that timeline — typically near completion — where the fire alarm system itself is formally tested and accepted, often witnessed by the AHJ."
    },
    {
      "q": "Do small projects need commissioning?",
      "a": "Every system needs acceptance testing. Formal commissioning per NFPA 3/4 is most valuable — and sometimes required — on complex or integrated projects (data centers, hospitals, high-rises). On a simple system, thorough acceptance testing may be all that's specified; check the project requirements and AHJ."
    }
  ],
  "category": "Testing",
  "readMins": 6,
  "published": "2026-09-27",
  "updated": "2026-09-27",
  "hero": "https://images.unsplash.com/photo-1761558794306-466448dab4bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxzYWZldHklMjBpbnNwZWN0aW9uJTIwY2hlY2tsaXN0JTIwY2xpcGJvYXJkfGVufDB8fHx8MTc5MDUyNjAxNHww&ixlib=rb-4.1.0&q=85",
  "heroAlt": "Inspector completing a fire system acceptance test checklist on a clipboard",
  "excerpt": "They sound interchangeable, but they answer different questions. One asks 'does this system work?' — the other asks 'does the whole design work together?'",
  "sections": [
    {
      "h": "Two questions, two scopes",
      "p": "Acceptance testing asks: does the installed fire alarm system do what it's supposed to, point by point? Commissioning asks a bigger question: does the entire life-safety design — across systems, over the project lifecycle — meet the owner's requirements? They overlap but are not the same, and confusing them leads to gaps at handover."
    },
    {
      "h": "Acceptance testing — NFPA 72 Chapter 14",
      "p": "Chapter 14 defines how the fire alarm system is inspected and tested. At acceptance, every initiating device, notification appliance, panel function, and interface is exercised and documented, and the results are compared to the approved design and sequence of operations. It's frequently witnessed by the AHJ and is the formal 'this system is accepted' milestone."
    },
    {
      "h": "Commissioning — NFPA 3 and NFPA 4",
      "p": "NFPA 3 defines commissioning as a lifecycle process with defined roles, documentation (OPR, BOD), and phases from design through occupancy. NFPA 4 covers integrated testing — verifying that fire alarm, suppression, HVAC, dampers, elevators, and access control perform together in the correct sequence. Commissioning is about the whole choreography, not a single instrument."
    },
    {
      "h": "How they work together",
      "p": "On an integrated project, acceptance testing of the fire alarm system is one input to the larger commissioning effort. The fire alarm is accepted per Chapter 14; then integrated testing per NFPA 4 confirms the cross-system sequence. The commissioning authority ties the evidence together into a final report the owner and AHJ can rely on."
    },
    {
      "h": "Getting the documentation right",
      "p": "Keep the records distinct and traceable: Chapter 14 acceptance test records for the fire alarm system, and a separate commissioning package (plan, checklists, integrated test procedure and results, issues log, final report) for the overall process. Reference the adopted code editions and confirm expectations with the AHJ and commissioning authority before handover."
    }
  ],
  "relatedModules": [
    "testing-maintenance",
    "compliance",
    "integration"
  ],
  "relatedGuides": [
    "data-center-fire-alarm-commissioning",
    "preparing-for-a-fire-alarm-inspection"
  ]
};
